import * as React from "react";
import { ApiSelectedFixedComment, SelectedFixedCommentMeta } from "../../../../misc/api/FixedComments/apiTypes";
import AsyncFixedCommentsButton from "../AsyncFixedComments/AsyncFixedCommentsButton";
import { AnalysisSetFixedComment, FixedCommentSet } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../../../../Core/CurrentStudyConfigurationContext";
import { FixedCommentSetTypeEnum } from "../../../../misc/api/FixedComments/FixedCommentSetTypes";
import { FixedCommentEntityTypeEnum } from "../../../../misc/api/FixedComments/FixedCommentSetEntityTypes";
import { SessionTypes } from "../../../../misc/api/ApiSessionTypes";
import { useWebPortalApiClient } from "../../../../misc/api/WebPortalTokenClient";

interface TechnicianSessionCommentsProps {
    AnalysisSetId: string;
    IsReadOnly?: boolean;
    SessionType: SessionTypes;
};

const TechnicianSessionComments = ({ AnalysisSetId, IsReadOnly, SessionType }: TechnicianSessionCommentsProps): JSX.Element => {
    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();

    const fetchComments = async (): Promise<SelectedFixedCommentMeta[]> => {

        let sessionComments: AnalysisSetFixedComment[] = [];

        switch (SessionType) {
            case SessionTypes.Fvc:
                sessionComments = (await client.client.getSpirometryAnalysisSet(AnalysisSetId, "fixedComments")).fixedComments;
                break;
            case SessionTypes.Svc:
                sessionComments = (await client.client.getSvcAnalysisSet(AnalysisSetId, "fixedComments")).fixedComments;
                break
        }

        const fixedCommentsResult = await client.client.getStudyFixedCommentSets(undefined, `studyId eq ${study.id} and sessionType eq '${SessionType}' and commentSetType eq '${FixedCommentSetTypeEnum.Technician}'`, undefined, `fixedCommentSets($expand=comments)`);

        const comments = fixedCommentsResult.value[0].fixedCommentSets.find(x => x.entityType === FixedCommentEntityTypeEnum.Session).comments;

        return comments.sort((a, b) => a.displayOrder - b.displayOrder)
            .map(f => ({
            ...f,
            IsSelected: sessionComments.map(ci => ci.fixedCommentId)?.includes(f.id) ?? false,
            FollowOnComment: sessionComments.find(ci => ci.fixedCommentId === f.id)?.followOnComment,
        }));
    };

    const updateComments = async (commentMutations: ApiSelectedFixedComment[]): Promise<void> => {
        const selectedComments = commentMutations.filter(x => x.IsSelected);

        await client.client.patchSpirometryAnalysisSet({
            fixedComments: selectedComments.map(cm => ({
                analysisSetId: AnalysisSetId,
                fixedCommentId: cm.Id,
            }))
        }, AnalysisSetId);
    };

    return (<AsyncFixedCommentsButton
        buttonText={IsReadOnly ? "Show" : "Open"}
        header="Technician Fixed Comments - Session"
        isReadOnly={IsReadOnly}
        fetchComments={fetchComments}
        updateComments={updateComments}
    />);
};

export default TechnicianSessionComments;
